import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'

import { ApiError } from '../../../lib/api'
import { AuthAlert } from '../components/AuthAlert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuthSession } from '../session'
import { AuthFormIntro } from '../components/AuthFormIntro'
import { AuthSplitShell } from '../components/AuthSplitShell'
import { AuthPasswordField } from '../components/AuthPasswordField'

const createLoginSchema = (t: (key: string) => string) =>
  z.object({
    email: z.email(t('auth.emailPlaceholder')),
    password: z.string().min(1, t('auth.passwordPlaceholder')),
    remember: z.boolean(),
  })

type LoginFormValues = z.infer<ReturnType<typeof createLoginSchema>>

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login, loginPending } = useAuthSession()
  const [submissionMessage, setSubmissionMessage] = useState<string | null>(null)

  const redirectTo = searchParams.get('redirectTo') || '/dashboard'

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(createLoginSchema(t)),
  })

  const onSubmit = handleSubmit(async (values) => {
    setSubmissionMessage(null)

    try {
      await login(values)
      navigate(redirectTo, { replace: true })
    } catch (error) {
      if (error instanceof ApiError) {
        if (error.code === 'AUTH_EMAIL_UNVERIFIED') {
          navigate(`/verify-email?email=${encodeURIComponent(values.email)}`, { replace: true })
          return
        }

        if (error.code === 'AUTH_USER_INACTIVE') {
          setSubmissionMessage(t('auth.errors.accountInactive'))
          return
        }

        const emailError = error.errors?.email?.[0]

        if (emailError) {
          setError('email', { message: emailError })
        }

        setSubmissionMessage(emailError ?? error.message ?? t('errors.loadFailed'))
        return
      }

      setSubmissionMessage(t('errors.loadFailed'))
    }
  })

  return (
    <AuthSplitShell>
      <div className="w-full space-y-8">
        <AuthFormIntro title={t('auth.signIn')} description={t('auth.loginSubtitle')} />

        <form className="space-y-5" onSubmit={onSubmit}>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="email">
              {t('auth.email')}
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder={t('auth.emailPlaceholder')}
              className="border-input px-4 text-foreground placeholder:text-muted-foreground"
              {...register('email')}
            />
            {errors.email ? <p className="text-sm text-rose-700">{errors.email.message}</p> : null}
          </div>

          <AuthPasswordField
            id="password"
            autoComplete="current-password"
            placeholder={t('auth.passwordPlaceholder')}
            label={t('auth.password')}
            action={
              <Link
                to="/password/forgot"
                className="text-sm font-medium text-muted-foreground transition hover:text-foreground"
              >
                {t('auth.forgotPassword')}
              </Link>
            }
            errorMessage={errors.password?.message}
            className="border-input px-4 text-foreground placeholder:text-muted-foreground"
            {...register('password')}
          />

          <label className="flex items-center gap-3 text-sm text-muted-foreground">
            <input
              type="checkbox"
              className="size-4 rounded border-input text-primary focus:ring-ring"
              {...register('remember')}
            />
            {t('auth.rememberMe')}
          </label>

          {submissionMessage ? (
            <AuthAlert variant="error">
              {submissionMessage}
            </AuthAlert>
          ) : null}

          <Button
            size="lg"
            type="submit"
            disabled={loginPending}
            className="w-full bg-foreground text-background hover:bg-foreground/90"
          >
            {loginPending ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>


      </div>
    </AuthSplitShell>
  )
}
